/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Keep hidden navigation imagery out of the critical loading path. Shopify's
 * native lazy-loading heuristic still fetches images that are positioned near
 * the viewport even when their mega menu is closed, so these images are
 * hydrated only when the related menu receives real user intent.
 */
(function () {
  'use strict';

  var deferredImageSelector = 'img[data-deferred-src]';

  function hydrateDeferredImages(root) {
    if (!root) return;

    root.querySelectorAll(deferredImageSelector).forEach(function (image) {
      var deferredSrcset = image.getAttribute('data-deferred-srcset');
      var deferredSrc = image.getAttribute('data-deferred-src');

      if (deferredSrcset) image.srcset = deferredSrcset;
      if (deferredSrc) image.src = deferredSrc;

      image.removeAttribute('data-deferred-srcset');
      image.removeAttribute('data-deferred-src');
    });
  }

  function handleMenuIntent(event) {
    var target = event.target;
    if (!(target instanceof Element)) return;

    var control = target.closest('[aria-controls]');
    if (control) {
      var controlledId = control.getAttribute('aria-controls');
      if (controlledId) hydrateDeferredImages(document.getElementById(controlledId));
    }

    var megaMenuContainer = target.closest('.has-mega-menu, .mega-menu');
    if (megaMenuContainer) hydrateDeferredImages(megaMenuContainer);
  }

  document.addEventListener('pointerover', handleMenuIntent, { passive: true });
  document.addEventListener('focusin', handleMenuIntent);
  document.addEventListener('click', handleMenuIntent, { passive: true });
})();

/**
 * Announce "added to cart" to screen readers.
 *
 * The theme's announcement is bound to the "product:added" event inside the Cart
 * component. PageFly product pages and app widgets post to /cart/add directly and
 * never dispatch that event, so this version also listens on the network layer.
 */
(function () {
  'use strict';

  var lastAnnounceAt = 0;

  function getLiveRegion() {
    var region = document.getElementById('a11y-live-region');
    if (region) return region;

    region = document.createElement('div');
    region.id = 'a11y-live-region';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    document.body.appendChild(region);
    return region;
  }

  function announceAdded() {
    var now = Date.now();
    // A single add-to-cart can fire both the event and a /cart/add request.
    if (now - lastAnnounceAt < 500) return;
    lastAnnounceAt = now;

    var message = (window.languages && window.languages.productAdded) || 'Added to cart';
    var region = getLiveRegion();

    // Clearing first keeps repeated identical messages announceable.
    region.textContent = '';
    window.setTimeout(function () {
      region.textContent = message;
    }, 100);
  }

  document.addEventListener('product:added', announceAdded);
  document.addEventListener('a11y:cart-added', announceAdded);

  var cartAddPattern = /\/cart\/add(\.js)?(\?|$)/;

  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var request = arguments[0];
      var url = typeof request === 'string' ? request : (request && request.url) || '';
      var result = originalFetch.apply(this, arguments);

      if (cartAddPattern.test(url)) {
        result.then(function (response) {
          if (response && response.ok) announceAdded();
        }).catch(function () {});
      }
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._a11yCartUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (cartAddPattern.test(String(xhr._a11yCartUrl || ''))) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) announceAdded();
        });
      }
      return originalSend.apply(this, arguments);
    };
  }
})();

/**
 * Fallback for opening the mini cart drawer.
 *
 * The theme opens the drawer from Cart#_onProductAdded, which only runs when the
 * "product:added" event is dispatched. Add-to-cart widgets that post to /cart/add
 * on their own never dispatch it, so the drawer stays shut. This listens for the
 * same signals the store already produces and, if the drawer is still closed a
 * moment later, opens it with the exact attributes the theme itself toggles.
 */
(function () {
  'use strict';

  var OPEN_DELAY = 700;

  var openTimer = null;
  var openedByFallback = false;
  var closeBound = false;

  function getForm() {
    return document.getElementById('mini-cart');
  }

  function getToggle() {
    return document.querySelector('.header__cart-toggle');
  }

  function isOpen(form) {
    return !!form && form.getAttribute('aria-hidden') === 'false';
  }

  function cartRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  function refreshContent(form) {
    return fetch(cartRoot() + 'cart?section_id=mini-cart', {
      credentials: 'same-origin',
      headers: { 'Cache-Control': 'no-cache' }
    })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        if (!html) return;

        var holder = document.createElement('div');
        holder.innerHTML = html;
        var fresh = holder.querySelector('#mini-cart');
        if (!fresh) return;

        form.innerHTML = fresh.innerHTML;
        var itemCount = fresh.getAttribute('data-item-count');
        if (itemCount !== null) form.setAttribute('data-item-count', itemCount);
      })
      .catch(function () {});
  }

  function applyMobileHeight(form) {
    if (window.innerWidth > 640) return;

    var rect = form.getBoundingClientRect();
    var maxHeight = Math.max(window.innerHeight - rect.top - 20, 220);
    form.style.maxHeight = maxHeight + 'px';

    var content = form.querySelector('.mini-cart__content');
    var recap = form.querySelector('.mini-cart__recap');
    if (content && recap) {
      content.style.maxHeight = Math.max(maxHeight - recap.clientHeight, 120) + 'px';
    }
  }

  function openDrawer() {
    var form = getForm();
    var toggle = getToggle();
    if (!form || isOpen(form)) return;

    refreshContent(form).then(function () {
      var currentForm = getForm();
      if (!currentForm) return;

      currentForm.setAttribute('aria-hidden', 'false');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('no-mobile-scroll');

      applyMobileHeight(currentForm);
      openedByFallback = true;
      bindClose();
    });
  }

  function closeDrawer() {
    var form = getForm();
    var toggle = getToggle();

    if (form) {
      form.setAttribute('aria-hidden', 'true');
      form.style.maxHeight = '';

      var content = form.querySelector('.mini-cart__content');
      if (content) content.style.maxHeight = '';
    }

    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('no-mobile-scroll');

    openedByFallback = false;
    unbindClose();
  }

  function onDocumentClick(event) {
    if (!openedByFallback) return;

    var target = event.target;
    if (!(target instanceof Element)) return;

    // Clicking the cart icon closes the drawer instead of letting the theme
    // reopen it from its own (out of sync) state.
    if (target.closest('.header__cart-toggle')) {
      event.preventDefault();
      event.stopPropagation();
      closeDrawer();
      return;
    }

    var form = getForm();
    if (form && form.contains(target)) return;
    closeDrawer();
  }

  function onKeydown(event) {
    if (event.key === 'Escape' || event.key === 'Esc') closeDrawer();
  }

  function bindClose() {
    if (closeBound) return;
    closeBound = true;
    document.addEventListener('click', onDocumentClick, true);
    document.addEventListener('keydown', onKeydown);
  }

  function unbindClose() {
    if (!closeBound) return;
    closeBound = false;
    document.removeEventListener('click', onDocumentClick, true);
    document.removeEventListener('keydown', onKeydown);
  }

  function scheduleOpen() {
    window.clearTimeout(openTimer);
    openTimer = window.setTimeout(function () {
      var form = getForm();
      // The theme opened it on its own in the meantime: leave it alone.
      if (form && !isOpen(form)) openDrawer();
    }, OPEN_DELAY);
  }

  document.addEventListener('product:added', scheduleOpen);
  document.addEventListener('a11y:cart-added', scheduleOpen);

  var cartAddPattern = /\/cart\/add(\.js)?(\?|$)/;

  if (window.fetch) {
    var originalFetch = window.fetch;
    window.fetch = function () {
      var request = arguments[0];
      var url = typeof request === 'string' ? request : (request && request.url) || '';
      var result = originalFetch.apply(this, arguments);

      if (cartAddPattern.test(url)) {
        result.then(function (response) {
          if (response && response.ok) scheduleOpen();
        }).catch(function () {});
      }
      return result;
    };
  }

  if (window.XMLHttpRequest) {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._cartOpenUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (cartAddPattern.test(String(xhr._cartOpenUrl || ''))) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) scheduleOpen();
        });
      }
      return originalSend.apply(this, arguments);
    };
  }

  // Last resort: watch the header badge, in case the cart is updated without a
  // request this script can see.
  var badge = document.querySelector('.header__cart-count');
  if (badge && window.MutationObserver) {
    var lastCount = parseInt(badge.textContent, 10) || 0;

    new MutationObserver(function () {
      var currentCount = parseInt(badge.textContent, 10) || 0;
      if (currentCount > lastCount) scheduleOpen();
      lastCount = currentCount;
    }).observe(badge, { childList: true, characterData: true, subtree: true });
  }
})();

/**
 * Keyboard support and cart-change detection for PageFly's add-to-cart widget.
 *
 * PageFly renders its own add-to-cart control and crashes while syncing the
 * Warehouse theme ("res is not defined" in pagefly-cart.js), so the theme never
 * learns about the addition. Pressing Enter also falls back to the browser's
 * native form submission, which navigates to the product page instead of adding.
 */
(function () {
  'use strict';

  var ADD_TO_CART_LABEL = /add to cart|add to bag|buy it now|加入购物车|加入購物車|立即购买/i;
  var CHECK_DELAY = 900;

  var lastCount = window.theme && typeof window.theme.cartCount === 'number' ? window.theme.cartCount : null;

  function cartPath(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  function getAddControl(target) {
    if (!target || !(target instanceof Element)) return null;

    var control = target.closest('button, a, [role="button"], input[type="submit"]');
    if (!control) return null;

    var label = (control.textContent || control.value || '').trim();
    return ADD_TO_CART_LABEL.test(label) ? control : null;
  }

  function notifyIfCartGrew() {
    fetch(cartPath('cart.js'), { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (cart) {
        if (!cart) return;

        if (lastCount === null) {
          lastCount = cart.item_count;
          return;
        }

        if (cart.item_count > lastCount) {
          lastCount = cart.item_count;
          document.dispatchEvent(new CustomEvent('a11y:cart-added', { bubbles: true }));
        }
      })
      .catch(function () {});
  }

  function scheduleCheck() {
    window.setTimeout(notifyIfCartGrew, CHECK_DELAY);
  }

  document.addEventListener('click', function (event) {
    if (getAddControl(event.target)) scheduleCheck();
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') return;

    var control = getAddControl(event.target);
    if (!control) return;

    // Enter on a submit button triggers the native form POST, and Enter on the
    // surrounding product link navigates away. Both bypass PageFly's handler.
    event.preventDefault();

    var form = control.closest('form');
    if (form) {
      var blockSubmit = function (submitEvent) { submitEvent.preventDefault(); };
      form.addEventListener('submit', blockSubmit);
      window.setTimeout(function () { form.removeEventListener('submit', blockSubmit); }, 1000);
    }

    if (control.tagName === 'A') {
      var blockNavigation = function (clickEvent) { clickEvent.preventDefault(); };
      control.addEventListener('click', blockNavigation, { once: true });
    }

    control.click();
    scheduleCheck();
  }, true);
})();
